import { useEffect } from "react";

import { prefereMenosMovimento } from "./textoDigitado";

/* GRAUS MÁXIMOS de inclinação em cada eixo.
   A faixa que a literatura de efeito tilt usa é 10 a 30, com 15 como valor comum.
   Aqui é 9, e o motivo é o conteúdo: estas cartas são ILUSTRAÇÕES com moldura
   desenhada, não elementos de UI plana. Passando de ~10 graus a moldura começa a
   parecer torta em vez de inclinada, e o efeito compete com a arte em vez de
   valorizá-la. */
const MAX_GRAUS = 9;

/* Distância do olho ao plano da carta. Quanto MENOR, mais dramática a perspectiva.
   1000px é o valor de referência dos tutoriais e é calibrado para cartões grandes de
   página inteira; 900 dá um pouco mais de profundidade numa carta de ~230px sem
   chegar à distorção de olho de peixe. */
const PERSPECTIVA_PX = 900;

/* A CLASSE QUE O HOOK ACENDE enquanto o cursor está sobre a carta. Neutra (`tl-`) e
   não do bloco `encerramento__`, e a diferença apareceu quando a coleção da área de
   conquistas passou a usar o mesmo efeito: com o nome do encerramento, ou a coleção
   escrevia classe de outra tela no próprio HTML, ou o hook ganhava um parâmetro que
   toda chamada teria de repetir. A classe é um DETALHE DE IMPLEMENTAÇÃO do efeito, e
   quem quiser o efeito só precisa declarar as duas regras dela (transição curta e
   brilho aceso) no próprio seletor. */
const CLASSE_INCLINANDO = "tl-inclinando";

/**
 * A inclinação para um ponto do cursor dentro de um retângulo. Função pura, para o
 * cálculo poder ser conferido sem navegador.
 *
 * OS EIXOS SÃO CRUZADOS de propósito: o movimento horizontal do mouse gira em torno
 * do eixo Y, e o vertical em torno do X. E o sinal do X é invertido — assim a carta
 * inclina EM DIREÇÃO ao cursor, como se ele a estivesse puxando. Sem a inversão ela
 * foge do dedo, que é o erro mais comum deste efeito e lê como algo empurrando.
 *
 * @param {DOMRect} rect  a caixa da carta na tela
 * @param {number}  x     clientX do ponteiro
 * @param {number}  y     clientY do ponteiro
 * @returns {{rotX: number, rotY: number, luzX: number, luzY: number}}
 *   graus nos dois eixos e a posição do brilho, em % da carta
 */
export function inclinacaoPara(rect, x, y) {
  if (!rect || !rect.width || !rect.height) {
    return { rotX: 0, rotY: 0, luzX: 50, luzY: 50 };
  }
  // 0 a 1 dentro da carta, preso nas bordas: o ponteiro pode estar num filho que
  // transborda, e um valor fora da faixa viraria uma inclinação absurda.
  const fx = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
  const fy = Math.min(1, Math.max(0, (y - rect.top) / rect.height));

  return {
    rotX: (0.5 - fy) * 2 * MAX_GRAUS,
    rotY: (fx - 0.5) * 2 * MAX_GRAUS,
    luzX: fx * 100,
    luzY: fy * 100
  };
}

/* O efeito é para MOUSE. Num aparelho de toque o `pointermove` só existe enquanto o
   dedo está na tela — ou seja, durante um gesto de arrastar —, e inclinar a carta ali
   brigaria com a rolagem da faixa. `hover: none` é a pergunta certa: ela distingue o
   aparelho que consegue apontar sem tocar. */
function temMouse() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  } catch (erro) {
    return false;
  }
}

/**
 * Inclina em 3D a carta sob o cursor, dentro de um contêiner.
 *
 * UM LISTENER NO CONTÊINER, e não um por carta: o álbum pode ter 22 delas, e a
 * delegação também resolve de graça o caso de a lista mudar enquanto a tela está
 * aberta.
 *
 * O QUE VAI PARA O CSS SÃO CUSTOM PROPERTIES, escritas direto no elemento. Passar
 * isto por estado do React re-renderizaria a árvore inteira a cada pixel de
 * movimento; o CSS lê as variáveis e o compositor faz o resto, sem tocar no DOM
 * além de um `style`.
 *
 * `requestAnimationFrame` com trava: `pointermove` dispara muito mais que 60 vezes
 * por segundo em mouse bom, e escrever a cada evento é trabalho jogado fora — o
 * navegador só pinta uma vez por quadro de qualquer jeito.
 *
 * @param {object}  ref     ref do contêiner (a faixa de cartas)
 * @param {string}  seletor o que conta como carta
 * @param {boolean} ativo   desligado enquanto a faixa está sendo arrastada
 */
export function useInclinacao3d(ref, seletor, ativo) {
  useEffect(() => {
    const faixa = ref.current;
    if (!faixa || !ativo) return undefined;
    // Movimento decorativo puro: quem pediu menos movimento não recebe nenhum.
    if (!temMouse() || prefereMenosMovimento()) return undefined;

    let carta = null;
    let ponto = null;
    let agendado = false;

    function limpar() {
      if (!carta) return;
      carta.style.removeProperty("--tl-tilt-x");
      carta.style.removeProperty("--tl-tilt-y");
      carta.style.removeProperty("--tl-tilt-luz-x");
      carta.style.removeProperty("--tl-tilt-luz-y");
      carta.classList.remove(CLASSE_INCLINANDO);
      carta = null;
    }

    function aplicar() {
      agendado = false;
      if (!carta || !ponto) return;
      const { rotX, rotY, luzX, luzY } = inclinacaoPara(
        carta.getBoundingClientRect(),
        ponto.x,
        ponto.y
      );
      carta.style.setProperty("--tl-tilt-x", `${rotX.toFixed(2)}deg`);
      carta.style.setProperty("--tl-tilt-y", `${rotY.toFixed(2)}deg`);
      carta.style.setProperty("--tl-tilt-luz-x", `${luzX.toFixed(1)}%`);
      carta.style.setProperty("--tl-tilt-luz-y", `${luzY.toFixed(1)}%`);
    }

    function aoMover(evento) {
      if (evento.pointerType && evento.pointerType !== "mouse") return;
      const alvo = evento.target.closest
        ? evento.target.closest(seletor)
        : null;

      if (alvo !== carta) {
        limpar();
        carta = alvo;
        if (carta) {
          // A classe encurta a transição enquanto o cursor está em cima: com a
          // transição longa de volta ao repouso, a carta persegue o mouse com
          // atraso e o efeito lê como elástico em vez de inclinação.
          carta.classList.add(CLASSE_INCLINANDO);
        }
      }
      if (!carta) return;

      ponto = { x: evento.clientX, y: evento.clientY };
      if (!agendado) {
        agendado = true;
        window.requestAnimationFrame(aplicar);
      }
    }

    faixa.addEventListener("pointermove", aoMover);
    faixa.addEventListener("pointerleave", limpar);
    return () => {
      faixa.removeEventListener("pointermove", aoMover);
      faixa.removeEventListener("pointerleave", limpar);
      limpar();
    };
  }, [ref, seletor, ativo]);
}

export default useInclinacao3d;
